import ReactECharts from "echarts-for-react";
import { groupBy, mapValues } from "lodash";
import Card from "../UI/Card";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { useBalanceSearch, useBalanceTypes } from "../../hooks/use-balances";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useStats from "../../hooks/use-stats";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import { BalanceTypeCategory } from "../../utils/constants/balances";
import type { Balance } from "../../models/balance";
import type { Property } from "../../models/property";
import type { Tenant } from "../../models/tenant";
import styles from "./LoyersPercusCharts.module.css";

interface LoyersPercusChartsProps {
  debut?: string;
  fin: string;
  propertyIds: number[];
  profileBailleurIds: number[];
}

interface ChartDatum {
  value: number;
  name: string;
}

const CHART_STYLE = { height: 300, width: 333 };

function sumBalances(balances: Balance[]): number {
  return balances.reduce((acc, balance) => acc + balance.montant, 0);
}

function prepareChartData(
  balances: Balance[],
  groupByFn: (balance: Balance) => number | undefined,
  nameGetter: (id: number) => string,
): ChartDatum[] {
  const aggregated = mapValues(groupBy(balances, groupByFn), sumBalances);
  return Object.entries(aggregated).map(([key, value]) => ({
    value,
    name: nameGetter(parseInt(key)),
  }));
}

function chartOptions(title: string, data: ChartDatum[]) {
  return {
    tooltip: {
      trigger: "item",
      formatter: (params: { name: string; value: number }) =>
        `${params.name}: ${currencyFormatter(params.value)}`,
    },
    title: {
      text: title,
      left: "center",
    },
    series: [
      {
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: false,
        label: {
          show: false,
          position: "center",
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 20,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
  };
}

export default function LoyersPercusCharts({
  debut,
  fin,
  propertyIds,
  profileBailleurIds,
}: LoyersPercusChartsProps) {
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };
  const { tenantsById } = useTenants({ withById: true }) as {
    tenantsById: Record<number, Tenant>;
  };
  const { loyerHcVsCharges, isLoading: isLoadingStats } = useStats(
    debut,
    fin,
    propertyIds,
    profileBailleurIds,
  ) as {
    loyerHcVsCharges?: Record<string, { charges: number; loyer_hc: number }>;
    isLoading: boolean;
  };
  const { balances } = useBalanceSearch({
    debut,
    fin,
    property_ids: propertyIds,
    profile_bailleur_ids: profileBailleurIds,
  }) as { balances: Balance[] };
  const { balanceTypesByCategory } = useBalanceTypes() as {
    balanceTypesByCategory: Record<string, { static_id: string }[]>;
  };

  const paymentTypeIds = (
    balanceTypesByCategory[BalanceTypeCategory.LOCATAIRE_PAIEMENT] ?? []
  ).map((balanceType) => balanceType.static_id);
  const tenantPayments = balances.filter((balance) =>
    paymentTypeIds.includes(balance.type),
  );

  const totalSum = sumBalances(tenantPayments);

  const chartDataByTenant = prepareChartData(
    tenantPayments,
    (balance) => balance.id_tenant,
    (id) => tenantsById[id]?.nom ?? `Tenant ${id}`,
  );

  const chartDataByProperty = prepareChartData(
    tenantPayments,
    (balance) => tenantsById[balance.id_tenant ?? -1]?.id_bien,
    (id) => propertiesById[id]?.nom ?? `Property ${id}`,
  );

  const totalCharges = loyerHcVsCharges
    ? Object.values(loyerHcVsCharges).reduce(
        (acc, item) => acc + item.charges,
        0,
      )
    : 0;
  const totalLoyerHc = loyerHcVsCharges
    ? Object.values(loyerHcVsCharges).reduce(
        (acc, item) => acc + item.loyer_hc,
        0,
      )
    : 0;

  const chartDataChargesVsLoyerHc: ChartDatum[] = isLoadingStats
    ? []
    : [
        { value: totalCharges, name: "Charges" },
        { value: totalLoyerHc, name: "Loyer HC" },
      ];

  return (
    <Card>
      <div className={styles.chartsRow}>
        <ReactECharts
          option={chartOptions(
            "Loyers perçus par locataire",
            chartDataByTenant,
          )}
          style={CHART_STYLE}
        />
        <ReactECharts
          option={chartOptions("Loyers perçus par bien", chartDataByProperty)}
          style={CHART_STYLE}
        />
        <ReactECharts
          option={chartOptions(
            "Loyer HC vs charges",
            chartDataChargesVsLoyerHc,
          )}
          style={CHART_STYLE}
        />
      </div>
      <Text className={styles.total}>
        Total : {currencyFormatter(totalSum)}
      </Text>
    </Card>
  );
}
